import { isArray } from "@huub-web/utils";

import type { MaximumMortgageWizardFormValues } from "../types/MaximumMortgageWizard";

/**
 * Parses URL search parameters and converts them into typed wizard form values
 *
 * @param searchParams - The URL search parameters (Record<string, string | string[] | undefined>)
 * @returns Partial wizard form values with properly typed fields
 */
export const parseSearchParamsToMaximumMortgageWizardValues = (
  searchParams: Record<string, string | string[] | undefined>,
): Partial<MaximumMortgageWizardFormValues> => {
  /**
   * Gets a single string value from search params, handling array values
   */
  const getValue = (key: string): string | undefined => {
    const value = searchParams[key];
    return isArray(value) ? value[0] : value;
  };

  /**
   * Gets a boolean value from search params
   */
  const getBooleanValue = (key: string): boolean => {
    const value = getValue(key);
    return value === "true";
  };

  const formValues: Partial<MaximumMortgageWizardFormValues> = {
    // Income
    income: getValue("income"),
    age: getValue("age") as MaximumMortgageWizardFormValues["age"],
    hasPartner: getBooleanValue("hasPartner"),
    partnerIncome: getValue("partnerIncome"),
    partnerAge: getValue(
      "partnerAge",
    ) as MaximumMortgageWizardFormValues["partnerAge"],

    // Purpose
    purpose: getValue("purpose") as MaximumMortgageWizardFormValues["purpose"],
    houseInMind: getValue(
      "houseInMind",
    ) as MaximumMortgageWizardFormValues["houseInMind"],
    houseProgress: getValue(
      "houseProgress",
    ) as MaximumMortgageWizardFormValues["houseProgress"],
    newHousePrice: getValue("newHousePrice"),
    newHouseEnergyLabel: getValue(
      "newHouseEnergyLabel",
    ) as MaximumMortgageWizardFormValues["newHouseEnergyLabel"],

    // Current house
    hasCurrentHouse: getBooleanValue("hasCurrentHouse"),
    currentHouseSellingPrice: getValue("currentHouseSellingPrice"),
    currentHouseMortgageDebt: getValue("currentHouseMortgageDebt"),
    currentHouseMortgageLender: getValue("currentHouseMortgageLender"),
    currentHouseMarketValue: getValue("currentHouseMarketValue"),
    currentHouseEnergyLabel: getValue(
      "currentHouseEnergyLabel",
    ) as MaximumMortgageWizardFormValues["currentHouseEnergyLabel"],

    // Financial obligations
    hasLoans: getBooleanValue("hasLoans"),
    loans: getValue("loans"),
    hasStudentDebt: getBooleanValue("hasStudentDebt"),
    studentDebt: getValue("studentDebt"),
    hasPrivateLease: getBooleanValue("hasPrivateLease"),
    privateLease: getValue("privateLease"),
    ownCapital: getValue("ownCapital"),

    // House selection
    walterAddressObjectID: getValue("walterAddressObjectID"),
  };

  return formValues;
};
